import { z } from "zod";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";

/** Limits apply before schema parsing, including to unrecognized CMS properties. */
export function isBoundedPageJson(input: unknown): boolean {
  let remainingNodes = 20_000;
  let remainingCharacters = 200_000;
  const ancestors = new Set<object>();
  const visit = (value: unknown, depth: number): boolean => {
    if (--remainingNodes < 0 || depth > 24) return false;
    if (value === null || value === undefined || typeof value === "boolean") return true;
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value === "string") {
      remainingCharacters -= value.length;
      return value.length <= 20_000 && remainingCharacters >= 0;
    }
    if (typeof value !== "object" || ancestors.has(value)) return false;
    const prototype = Object.getPrototypeOf(value);
    // Structured clones may come from another realm with a different Object.prototype.
    if (!Array.isArray(value) && prototype !== null && Object.getPrototypeOf(prototype) !== null) {
      return false;
    }
    if (Array.isArray(value) && value.length > 500) return false;
    if (Object.getOwnPropertySymbols(value).length) return false;
    const descriptors = Object.getOwnPropertyDescriptors(value);
    const entries = Object.entries(descriptors).filter(
      ([key]) => !Array.isArray(value) || key !== "length"
    );
    if (entries.length > (Array.isArray(value) ? 500 : 32)) return false;
    ancestors.add(value);
    const valid = entries.every(
      ([key, descriptor]) =>
        key.length <= 100 &&
        (!Array.isArray(value) || /^(0|[1-9]\d*)$/.test(key)) &&
        "value" in descriptor &&
        visit(descriptor.value, depth + 1)
    );
    ancestors.delete(value);
    return valid;
  };
  try {
    return visit(input, 0);
  } catch {
    return false;
  }
}

/** Media/source links are web URLs or same-site paths, never data/script URLs. */
function isSafeWebUrl(value: string): boolean {
  if (!isSafeFormFunnelContentHref(value) || value !== value.trim()) return false;
  if (value.startsWith("/")) return true;
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password;
  } catch {
    return false;
  }
}

export const pageContentWebUrlSchema = z
  .string()
  .min(1)
  .max(2_048)
  .refine(isSafeWebUrl, "Use a safe web URL.");
/** Payload's Postgres adapter returns null for cleared optional fields. */
function optionalContentField<T extends z.ZodType>(schema: T) {
  return schema.nullish().transform((value) => value ?? undefined);
}

function omitUndefinedFields<T extends object>(value: T): T {
  Object.keys(value).forEach((key) => {
    if (Reflect.get(value, key) === undefined) Reflect.deleteProperty(value, key);
  });
  return value;
}

const media = z
  .strictObject({
    id: z
      .string()
      .min(1)
      .max(128)
      .refine((value) => Boolean(value.trim()), "Use a CMS media ID."),
    url: pageContentWebUrlSchema,
    // An empty alt is an intentional decorative-image choice.
    alt: z.string().max(1_000),
    width: optionalContentField(z.number().int().positive().max(20_000)),
    height: optionalContentField(z.number().int().positive().max(20_000)),
  })
  .transform(omitUndefinedFields);

/** Payload array/block row IDs are editor metadata, never semantic content identity. */
export function pageCmsRow<T extends z.ZodRawShape>(shape: T) {
  return z
    .strictObject({ ...shape, id: z.string().max(128).nullable().optional() })
    .transform((row) => {
      Reflect.deleteProperty(row, "id");
      return row;
    });
}

const elementProperties = [
  "type",
  "children",
  "direction",
  "format",
  "indent",
  "version",
  "textFormat",
  "textStyle",
];
const inlineTypes = new Set(["text", "linebreak", "link", "autolink"]);
const rootTypes = new Set([
  "paragraph",
  "heading",
  "quote",
  "list",
  "horizontalrule",
  "table",
  "upload",
]);
const alignments = ["", "left", "center", "right", "justify", "start", "end"];
const uploadMediaId = z.uuid();

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function hasOnlyKeys(value: Record<string, unknown>, keys: string[]): boolean {
  return Object.keys(value).every((key) => keys.includes(key));
}

function optionalInteger(value: unknown, maximum: number): boolean {
  return (
    value === undefined ||
    (Number.isInteger(value) && Number(value) >= 0 && Number(value) <= maximum)
  );
}

/** The bounded native Lexical features shared by the funnel reader and page editor. */
function isSafeRichText(input: unknown): input is Record<string, unknown> {
  if (!isRecord(input) || !hasOnlyKeys(input, ["root"]) || !isRecord(input.root)) return false;
  let remainingNodes = 500;
  const visit = (value: unknown, depth: number, parentType?: string): boolean => {
    if (--remainingNodes < 0 || depth > 12 || !isRecord(value)) return false;
    const type = value.type;
    if (
      typeof type !== "string" ||
      (value.version !== undefined && value.version !== (type === "upload" ? 3 : 1))
    )
      return false;
    if (parentType === undefined && type !== "root") return false;
    if (parentType === "root" && !rootTypes.has(type)) return false;
    if (parentType === "list" && type !== "listitem") return false;
    if (parentType === "table" && type !== "tablerow") return false;
    if (parentType === "tablerow" && type !== "tablecell") return false;
    if (parentType === "tablecell" && (!rootTypes.has(type) || type === "table")) return false;
    if (
      parentType &&
      !["root", "list", "listitem", "table", "tablerow", "tablecell"].includes(parentType) &&
      !inlineTypes.has(type)
    )
      return false;
    if (parentType === "listitem" && !inlineTypes.has(type) && type !== "list") return false;
    if (["link", "autolink"].includes(parentType ?? "") && ["link", "autolink"].includes(type))
      return false;

    if (type === "text") {
      return (
        hasOnlyKeys(value, ["type", "text", "detail", "format", "mode", "style", "version"]) &&
        typeof value.text === "string" &&
        value.text.length <= 20_000 &&
        optionalInteger(value.detail, 3) &&
        optionalInteger(value.format, 2_047) &&
        (value.mode === undefined ||
          (typeof value.mode === "string" &&
            ["normal", "token", "segmented"].includes(value.mode))) &&
        (value.style === undefined || value.style === "")
      );
    }
    if (type === "linebreak" || type === "horizontalrule") {
      return hasOnlyKeys(value, ["type", "version"]);
    }
    if (type === "upload") {
      // Saved designs own relationships, never inline media documents or executable URLs.
      // Resolve IDs against the media collection only when rendering.
      return (
        hasOnlyKeys(value, ["type", "version", "id", "format", "relationTo", "fields", "value"]) &&
        value.version === 3 &&
        value.relationTo === "media" &&
        uploadMediaId.safeParse(value.value).success &&
        (value.id === undefined || (typeof value.id === "string" && value.id.length <= 128)) &&
        (value.format === undefined ||
          (typeof value.format === "string" && alignments.includes(value.format))) &&
        (value.fields === undefined ||
          value.fields === null ||
          (isRecord(value.fields) &&
            hasOnlyKeys(value.fields, ["alt"]) &&
            (value.fields.alt === undefined ||
              (typeof value.fields.alt === "string" && value.fields.alt.length <= 1_000))))
      );
    }

    const extraProperties: Record<string, string[]> = {
      root: [],
      paragraph: [],
      quote: [],
      heading: ["tag"],
      list: ["listType", "start", "tag"],
      listitem: ["value", "checked"],
      link: ["fields", "id"],
      autolink: ["fields", "id", "isUnlinked"],
      table: ["colWidths", "rowStriping", "frozenColumnCount", "frozenRowCount"],
      tablerow: ["height"],
      tablecell: ["colSpan", "rowSpan", "headerState", "width", "backgroundColor", "verticalAlign"],
    };
    const extras = extraProperties[type];
    if (
      !extras ||
      !hasOnlyKeys(value, [...elementProperties, ...extras]) ||
      !Array.isArray(value.children)
    )
      return false;
    if (
      value.direction !== undefined &&
      value.direction !== null &&
      (typeof value.direction !== "string" || !["ltr", "rtl"].includes(value.direction))
    )
      return false;
    if (
      value.format !== undefined &&
      (typeof value.format !== "string" || !alignments.includes(value.format))
    )
      return false;
    // Payload adds fixed 40px padding for other elements; list-item indent is
    // nesting metadata and does not receive that padding in its JSX converter.
    if (
      !optionalInteger(value.indent, type === "listitem" ? 12 : 0) ||
      !optionalInteger(value.textFormat, 2_047)
    )
      return false;
    if (value.textStyle !== undefined && value.textStyle !== "") return false;
    if (type === "table") {
      if (
        (value.colWidths !== undefined &&
          (!Array.isArray(value.colWidths) ||
            value.colWidths.length > 50 ||
            value.colWidths.some(
              (width) => !Number.isFinite(width) || width <= 0 || width > 20_000
            ))) ||
        (value.rowStriping !== undefined && typeof value.rowStriping !== "boolean") ||
        !optionalInteger(value.frozenColumnCount, 50) ||
        !optionalInteger(value.frozenRowCount, 500)
      )
        return false;
    }
    if (type === "tablerow" && !optionalInteger(value.height, 20_000)) return false;
    if (type === "tablecell") {
      if (
        !optionalInteger(value.headerState, 3) ||
        !optionalInteger(value.colSpan, 50) ||
        value.colSpan === 0 ||
        !optionalInteger(value.rowSpan, 500) ||
        value.rowSpan === 0 ||
        !optionalInteger(value.width, 20_000) ||
        (value.backgroundColor !== undefined &&
          value.backgroundColor !== null &&
          (typeof value.backgroundColor !== "string" ||
            !/^(?:#[\da-fA-F]{3,8}|[a-zA-Z]{1,32}|(?:rgb|hsl)a?\([\d.,%\s+-]{1,80}\))$/.test(
              value.backgroundColor
            ))) ||
        (value.verticalAlign !== undefined &&
          !["top", "middle", "bottom"].includes(String(value.verticalAlign)))
      )
        return false;
    }
    if (
      type === "heading" &&
      (typeof value.tag !== "string" || !["h1", "h2", "h3", "h4", "h5", "h6"].includes(value.tag))
    )
      return false;
    if (type === "list") {
      if (
        typeof value.listType !== "string" ||
        !["bullet", "number", "check"].includes(value.listType)
      )
        return false;
      if (
        value.tag !== (value.listType === "number" ? "ol" : "ul") ||
        !optionalInteger(value.start, 1_000_000)
      )
        return false;
    }
    if (
      type === "listitem" &&
      (!optionalInteger(value.value, 1_000_000) ||
        (value.checked !== undefined && typeof value.checked !== "boolean"))
    )
      return false;
    if (type === "link" || type === "autolink") {
      const fields = value.fields;
      if (!isRecord(fields) || !hasOnlyKeys(fields, ["linkType", "url", "newTab", "doc"]))
        return false;
      if (
        fields.linkType !== "custom" ||
        typeof fields.url !== "string" ||
        fields.url.length > 2_048 ||
        !isSafeFormFunnelContentHref(fields.url)
      )
        return false;
      if (fields.doc !== undefined && fields.doc !== null) return false;
      if (fields.newTab !== undefined && typeof fields.newTab !== "boolean") return false;
      if (value.id !== undefined && (typeof value.id !== "string" || value.id.length > 128))
        return false;
      if (value.isUnlinked !== undefined && typeof value.isUnlinked !== "boolean") return false;
    }
    return value.children.every((child) => visit(child, depth + 1, type));
  };
  return visit(input.root, 0);
}

export const pageContentSchemas = {
  heading: z
    .strictObject({
      text: z.string().max(500),
      eyebrow: optionalContentField(z.string().max(200)),
      description: optionalContentField(z.string().max(2_000)),
    })
    .transform(omitUndefinedFields),
  richText: z.strictObject({
    content: z.custom<Record<string, unknown>>(
      isSafeRichText,
      "Use supported, bounded Lexical text content."
    ),
  }),
  image: z
    .strictObject({ image: media, caption: optionalContentField(z.string().max(2_000)) })
    .transform(omitUndefinedFields),
  stats: z
    .strictObject({
      eyebrow: optionalContentField(z.string().max(200)),
      title: optionalContentField(z.string().max(500)),
      items: z
        .array(
          pageCmsRow({
            value: z.union([z.string().max(100), z.number().finite()]),
            label: z.string().max(500),
            icon: optionalContentField(z.enum(["wage", "growth", "openings"])),
          }).transform(omitUndefinedFields)
        )
        .max(12),
      sourceText: optionalContentField(z.string().max(2_000)),
      sourceUrl: optionalContentField(pageContentWebUrlSchema),
      sourceLinkLabel: optionalContentField(z.string().max(200)),
    })
    .transform(omitUndefinedFields),
  testimonial: z
    .strictObject({
      quote: z.string().max(5_000),
      name: z.string().max(200),
      photo: optionalContentField(media),
    })
    .transform(omitUndefinedFields),
};
